import { zodResolver } from '@hookform/resolvers/zod'
import { CircleCheckIcon } from 'lucide-react'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Parameter, ParameterValue } from '@/interfaces/catalog/Parameter'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

import { ParameterForm } from './ParameterForm'
import { ParameterFormSchema, ParameterFormValues } from './schema'

interface ParameterFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  parameter?: Parameter | null
}

const czkAmount = (value: ParameterValue) => {
  const amount = value.absolutePrices?.find((price) => price.currency === 'CZK')?.amount

  return amount === undefined ? null : parseInt(amount, 10) / 100
}

export const ParameterFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  parameter,
}: ParameterFormModalProps) => {
  const mode = parameter ? 'edit' : 'new'
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog.parameters',
  })

  const methods = useForm<ParameterFormValues>({
    resolver: zodResolver(ParameterFormSchema),
    defaultValues: {
      name: '',
      code: '',
      valueType: 'select',
      isDescriptive: false,
      isFilterable: false,
      isSurcharge: false,
      values: [],
    },
  })

  useEffect(() => {
    if (!isOpen) {
      return
    }

    methods.reset({
      name: parameter?.name ?? '',
      code: parameter?.code ?? '',
      valueType: parameter?.valueType ?? 'select',
      isDescriptive: parameter?.isDescriptive ?? false,
      isFilterable: parameter?.isFilterable ?? false,
      isSurcharge: parameter?.isSurcharge ?? false,
      values:
        parameter?.values.map((value) => ({
          id: value.id,
          code: value.code,
          label: value.label,
          percentOfBase: value.percentOfBase,
          absolutePriceCzk: czkAmount(value),
        })) ?? [],
    })
  }, [isOpen, methods, parameter])

  const onValid = async (values: ParameterFormValues) => {
    const body = {
      name: values.name,
      code: values.code,
      valueType: values.valueType,
      isDescriptive: values.isDescriptive,
      isFilterable: values.isFilterable,
      isSurcharge: values.isSurcharge,
      values: values.values.map((value, index) => {
        // The form edits only CZK, so keep the other currencies of a saved value
        const otherPrices =
          parameter?.values
            .find((saved) => saved.id === value.id)
            ?.absolutePrices?.filter((price) => price.currency !== 'CZK') ?? []
        const czkPrice =
          value.absolutePriceCzk !== null &&
          value.absolutePriceCzk !== undefined &&
          !Number.isNaN(value.absolutePriceCzk)
            ? [
                {
                  amount: Math.round(value.absolutePriceCzk * 100).toString(),
                  currency: 'CZK',
                },
              ]
            : []
        const absolutePrices = [...czkPrice, ...otherPrices]

        return {
          id: value.id,
          code: value.code,
          label: value.label,
          sortOrder: index,
          percentOfBase: value.percentOfBase || null,
          absolutePrices: absolutePrices.length > 0 ? absolutePrices : null,
        }
      }),
    }

    try {
      if (parameter) {
        await API.patch<Parameter>(`/api/v2/parameters/${parameter.id}`, body)
      } else {
        await API.post<Parameter>('/api/v2/parameters', body)
      }

      toast.success(t(`${mode}.success`))
      onSuccess?.()
      onClose()
      methods.reset()
    } catch (error: unknown) {
      console.error('Parameter form error:', error)
      toast.error(t(`${mode}.error`), {
        description: extractErrorMessage(error) || t('error.description'),
      })
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
      methods.reset()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(`${mode}.title`)}</DialogTitle>
        </DialogHeader>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onValid)}
            className="w-full space-y-6"
          >
            <ParameterForm isEdit={Boolean(parameter)} />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('form.cancel')}
              </Button>
              <Button type="submit" className="text-md">
                <CircleCheckIcon className="size-5 mr-1" color="white" />
                {t('form.save')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
